/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import {
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
  Badge,
  Button
} from 'react-bootstrap'
import Loader from '../../components/atoms/Loader'
import RowEmpty from '../../components/atoms/RowEmpty'
import TablePagination from '../../components/atoms/Table/TablePagination'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router-dom'
import api from '../../config/AxiosInstance'
import styles from '../Layout/dashboard.module.css'
import { ChevronRight, BarChart2 } from 'lucide-react'

const HomeDashboard = () => {
  const [warehouses, setWarehouses] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(null)
  const [inboundFilter, setInboundFilter] = useState('Today')
  const [outboundFilter, setOutboundFilter] = useState('Today')
  const [inboundStats, setInboundStats] = useState({
    plan: 0,
    onProgress: 0,
    received: 0
  })
  const [outboundStats, setOutboundStats] = useState({
    plan: 0,
    onProgress: 0,
    received: 0
  })
  const [expiredItems, setExpiredItems] = useState([])
  const [stagings, setStagings] = useState([])
  const [productivity, setProductivity] = useState([])
  const [prodFilter, setProdFilter] = useState('Today')
  const [prodRoleFilter, setProdRoleFilter] = useState('All')
  const [prodLoading, setProdLoading] = useState(false)
  const [prodMeta, setProdMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [loading, setLoading] = useState(false)
  const [slotCapacity, setSlotCapacity] = useState(null)
  const [slotCapacityLoading, setSlotCapacityLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    fetchWarehouses()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (selectedWarehouse) {
        setLoading(true)
        try {
          await Promise.all([
            fetchStagings(),
            fetchExpiredItems(),
            fetchInboundStats(),
            fetchOutboundStats(),
            fetchSlotCapacity()
          ])
        } catch (error) {
          console.error(error)
        } finally {
          setLoading(false)
        }
      }
    }
    fetchData()
  }, [selectedWarehouse, inboundFilter, outboundFilter])

  const fetchInboundStats = async () => {
    try {
      const res = await api.get(
        `/dashboard/inbound-stats?warehouse_id=${selectedWarehouse}&filter=${inboundFilter}`
      )
      if (res.data.success && res.data.data) {
        setInboundStats({
          plan: res.data.data.plan,
          onProgress: res.data.data.on_progress,
          received: res.data.data.received
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchOutboundStats = async () => {
    try {
      const res = await api.get(
        `/dashboard/outbound-stats?warehouse_id=${selectedWarehouse}&filter=${outboundFilter}`
      )
      if (res.data.success && res.data.data) {
        setOutboundStats({
          plan: res.data.data.plan,
          onProgress: res.data.data.on_progress,
          received: res.data.data.received
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchWarehouses = async () => {
    try {
      const res = await api.get(
        '/master/warehouses/all?is_parent=true&forUser=1&showAll=true'
      )
      if (res.data.success && res.data.data) {
        setWarehouses(res.data.data)
        if (res.data.data?.length > 0) {
          setSelectedWarehouse(res.data.data[0].id)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchStagings = async () => {
    try {
      const warehouse = warehouses.find(
        (w) => String(w.id) === String(selectedWarehouse)
      )
      const res = await api.get(
        `/dashboard/stagings?dest_warehouse=${warehouse?.warehouse_id || ''}`
      )
      if (res.data.success && res.data.data) {
        setStagings(res.data.data)
      } else {
        setStagings([])
      }
    } catch (error) {
      console.error(error)
      setStagings([])
    }
  }

  const fetchExpiredItems = async () => {
    try {
      const res = await api.get(
        `/dashboard/expired?warehouse_id=${selectedWarehouse}`
      )
      if (res.data.success && res.data.data) {
        setExpiredItems(res.data.data)
      } else {
        setExpiredItems([])
      }
    } catch (error) {
      console.error(error)
      setExpiredItems([])
    }
  }

  const fetchSlotCapacity = async () => {
    try {
      setSlotCapacityLoading(true)
      const res = await api.get(
        `/dashboard/slot-capacity?warehouse_id=${selectedWarehouse}`
      )
      if (res.data.success && res.data.data) {
        setSlotCapacity(res.data.data)
      } else {
        setSlotCapacity(null)
      }
    } catch (error) {
      console.error(error)
      setSlotCapacity(null)
    } finally {
      setSlotCapacityLoading(false)
    }
  }

  const fetchProductivity = async () => {
    try {
      setProdLoading(true)
      const res = await api.get('/dashboard/productivity-stats', {
        params: {
          warehouse_id: selectedWarehouse,
          filter: prodFilter,
          per_page: prodMeta.per_page,
          page: prodMeta.current_page,
          role: prodRoleFilter !== 'All' ? prodRoleFilter : ''
        }
      })
      if (res.data.success && res.data.data) {
        setProductivity(res.data.data)
        if (res.data.metadata) {
          setProdMeta(res.data.metadata)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setProdLoading(false)
    }
  }

  const handlePageChangeProductivity = (data) => {
    setProdMeta((prev) => ({
      ...prev,
      current_page: parseInt(data.selected + 1)
    }))
  }

  const handleOnChangeMetaProductivity = (e) => {
    setProdMeta((prev) => ({
      ...prev,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  useEffect(() => {
    if (selectedWarehouse) {
      fetchProductivity()
    }
  }, [
    selectedWarehouse,
    prodFilter,
    prodRoleFilter,
    prodMeta.current_page,
    prodMeta.per_page
  ])

  return (
    <SkeletonTheme baseColor="#f3f3f3" highlightColor="#ecebeb">
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3
              className="fw-bold"
              style={{ color: 'var(--primary-orange)', fontSize: '24px' }}
            >
              Dashboard
            </h3>
          </Col>
          <Col className="text-end d-flex justify-content-end align-items-center gap-3">
            <Form.Select
              style={{
                width: '250px',
                borderRadius: '10px',
                height: '45px',
                border: '1px solid #e9ecef',
                fontSize: '0.9rem'
              }}
              value={selectedWarehouse || ''}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_name}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Row>
          <Col lg={9}>
            <SectionHeader
              title="INBOUND PLAN"
              filter={inboundFilter}
              setFilter={setInboundFilter}
            />
            <Row className="mb-4">
              <StatCard
                title="PLAN"
                value={inboundStats.plan}
                loading={loading}
              />
              <StatCard
                title="ON PROGRESS"
                value={inboundStats.onProgress}
                color="text-primary"
                loading={loading}
              />
              <StatCard
                title="RECEIVED"
                value={inboundStats.received}
                color="text-warning"
                loading={loading}
              />
            </Row>

            <SectionHeader
              title="OUTBOUND PLAN"
              filter={outboundFilter}
              setFilter={setOutboundFilter}
            />
            <Row className="mb-4">
              <StatCard
                title="PLAN"
                value={outboundStats.plan}
                loading={loading}
              />
              <StatCard
                title="ON PROGRESS"
                value={outboundStats.onProgress}
                color="text-primary"
                loading={loading}
              />
              <StatCard
                title="RECEIVED"
                value={outboundStats.received}
                color="text-warning"
                loading={loading}
              />
            </Row>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-secondary fw-bold small m-0">
                  LIST ITEM EXPIRED
                </h6>
                <Button
                  variant="link"
                  className="text-decoration-none p-0 small fw-bold d-flex align-items-center"
                  style={{ color: 'var(--primary-orange)' }}
                  onClick={() =>
                    navigate('/item-issue', {
                      state: {
                        warehouse_id: warehouses.find(
                          (w) => String(w.id) === String(selectedWarehouse)
                        )
                          ? {
                              value: selectedWarehouse,
                              label: warehouses.find(
                                (w) =>
                                  String(w.id) === String(selectedWarehouse)
                              ).warehouse_name
                            }
                          : null
                      }
                    })
                  }
                >
                  View All <ChevronRight size={16} />
                </Button>
              </div>
              <Row xs={1} md={1} className="g-3">
                {loading ? (
                  [1, 2].map((i) => (
                    <Col key={i}>
                      <ExpiredItemCard loading={true} />
                    </Col>
                  ))
                ) : expiredItems?.length > 0 ? (
                  expiredItems.map((item, idx) => (
                    <Col key={idx}>
                      <ExpiredItemCard item={item} />
                    </Col>
                  ))
                ) : (
                  <Col>
                    <Card className="border-0 shadow-sm p-3 text-center text-muted">
                      No expired items found
                    </Card>
                  </Col>
                )}
              </Row>
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-secondary fw-bold small m-0 d-flex align-items-center gap-2">
                  <BarChart2 size={15} />
                  SLOT CAPACITY
                </h6>
              </div>
              <SlotCapacitySection
                data={slotCapacity}
                loading={slotCapacityLoading || loading}
              />
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-secondary fw-bold small m-0 uppercase">
                  PRODUCTIVITY WAREHOUSE
                </h6>
                <div className="d-flex gap-2">
                  <Form.Select
                    size="sm"
                    style={{ width: '120px', borderRadius: '8px' }}
                    value={prodFilter}
                    onChange={(e) => {
                      setProdFilter(e.target.value)
                      setProdMeta((prev) => ({ ...prev, current_page: 1 }))
                    }}
                  >
                    <option value="Today">Today</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Yearly">Yearly</option>
                  </Form.Select>
                  <Form.Select
                    size="sm"
                    style={{ width: '100px', borderRadius: '8px' }}
                    value={prodRoleFilter}
                    onChange={(e) => {
                      setProdRoleFilter(e.target.value)
                      setProdMeta((prev) => ({ ...prev, current_page: 1 }))
                    }}
                  >
                    <option value="All">All</option>
                    <option value="PNP">PNP</option>
                    <option value="RNR">RNR</option>
                  </Form.Select>
                </div>
              </div>
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Table hover responsive className="mb-0">
                  <thead className="bg-light">
                    <tr className="small text-secondary">
                      <th>Name</th>
                      <th>Tiket Retrieved</th>
                      <th>Tiket Done</th>
                      <th>Avg Processing Time</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody className="small">
                    {prodLoading ? (
                      <tr>
                        <td colSpan={5} className="text-center p-4">
                          <Loader />
                        </td>
                      </tr>
                    ) : productivity?.length > 0 ? (
                      productivity.map((p, idx) => (
                        <tr key={idx}>
                          <td className="fw-medium">{p.name}</td>
                          <td>{p.total_retrieved}</td>
                          <td>{p.total_done}</td>
                          <td>{p.avg_processing_time}</td>
                          <td>
                            <Badge
                              bg={
                                p.role?.toUpperCase() === 'PNP'
                                  ? 'secondary'
                                  : 'dark'
                              }
                              className="rounded-pill px-3 text-uppercase"
                            >
                              {p.role}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <RowEmpty colSpan={5} />
                    )}
                  </tbody>
                </Table>
                {productivity?.length > 0 && (
                  <div className="p-3 border-top">
                    <TablePagination
                      meta={prodMeta}
                      handleOnChangeMeta={handleOnChangeMetaProductivity}
                      handlePageChange={handlePageChangeProductivity}
                    />
                  </div>
                )}
              </Card>
            </div>
          </Col>

          <Col lg={3}>
            <h6 className="text-secondary fw-bold mb-3 small uppercase">
              STAGING AREA
            </h6>
            <Row className="g-3">
              {loading ? (
                [1, 2, 3, 4].map((i) => (
                  <Col xs={12} md={6} lg={12} key={i}>
                    <Card
                      className={`border-0 shadow-sm ${styles.stagingCard}`}
                    >
                      <Card.Body className="p-3">
                        <Skeleton width="60%" height={15} className="mb-2" />
                        <Skeleton width="100%" height={30} />
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : stagings && stagings.length > 0 ? (
                stagings.map((s, idx) => (
                  <Col xs={12} md={6} lg={12} key={idx}>
                    <Card
                      className={`border-0 shadow-sm ${styles.stagingCard} ${
                        s.is_used ? styles.stagingCardActive : ''
                      }`}
                      onClick={() => {
                        if (s.is_used && s.activity_type && s.reference_id) {
                          let path = ''
                          if (s.source_type === 'outbound') {
                            path = `/outbound/plan/detail/${s.reference_id}`
                          } else if (s.source_type === 'mr') {
                            path = `/inbound/material-reject/detail/${s.reference_id}`
                          } else if (s.source_type === 'ir') {
                            path = `/inbound/inbound-return/detail/${s.reference_id}`
                          } else if (s.source_type === 'ml') {
                            path = `/inbound/ml/detail/${s.reference_id}`
                          } else {
                            path =
                              s.activity_type === 'inbound'
                                ? `/inbound/purchase-receipt/${s.reference_id}`
                                : `/outbound/plan/detail/${s.reference_id}`
                          }

                          if (path) {
                            window.open(path, '_blank')
                          }
                        }
                      }}
                    >
                      <Card.Body className="p-3 d-flex justify-content-between align-items-center">
                        <div
                          className="d-flex flex-column gap-2"
                          style={{ flex: 1 }}
                        >
                          <div
                            className="fw-bold"
                            style={{ fontSize: '14px', color: '#2c3e50' }}
                          >
                            {s.name}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Badge
                              bg={s.is_used ? 'success' : 'light'}
                              text={s.is_used ? 'white' : 'dark'}
                              className={`py-1 px-2 ${styles.stagingBadge}`}
                              style={{
                                fontSize: '10px',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}
                            >
                              {s.is_used ? 'IN USE' : 'AVAILABLE'}
                            </Badge>
                            {s.is_used && s.activity_type && (
                              <Badge
                                bg={
                                  s.activity_type === 'inbound'
                                    ? 'warning'
                                    : 'primary'
                                }
                                text={
                                  s.activity_type === 'inbound'
                                    ? 'dark'
                                    : 'white'
                                }
                                className={`py-1 px-2 ${styles.stagingBadge}`}
                                style={{
                                  fontSize: '10px',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}
                              >
                                {s.activity_type.toUpperCase()}
                              </Badge>
                            )}
                          </div>
                          {s.is_used && s.ticket_no && (
                            <div
                              style={{
                                color: 'var(--primary-orange)',
                                fontSize: '12px',
                                fontWeight: '600',
                                letterSpacing: '0.2px'
                              }}
                              className="mt-1"
                            >
                              {s.ticket_no}
                            </div>
                          )}
                        </div>
                        {s.is_used && (
                          <ChevronRight
                            size={16}
                            className="text-muted opacity-50 ms-2"
                          />
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <Card
                    className={`border-0 shadow-sm p-3 text-center text-muted small ${styles.stagingCard}`}
                  >
                    No staging area found
                  </Card>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Container>
    </SkeletonTheme>
  )
}

const SectionHeader = ({ title, filter, setFilter }) => (
  <div className="d-flex justify-content-between align-items-center mb-3">
    <h6 className="text-secondary fw-bold small m-0">{title}</h6>
    <Form.Select
      size="sm"
      style={{ width: '120px', borderRadius: '8px' }}
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
    >
      <option value="Today">Today</option>
      <option value="Weekly">Weekly</option>
      <option value="Monthly">Monthly</option>
      <option value="Yearly">Yearly</option>
    </Form.Select>
  </div>
)

const StatCard = ({ title, value, color = 'text-dark', loading = false }) => (
  <Col md={4}>
    <Card
      className={`border-0 shadow-sm rounded-4 h-100 ${styles.statCard} ${!loading ? styles.fadeIn : ''}`}
    >
      <Card.Body className="p-4">
        <div className="text-secondary small fw-bold mb-2 uppercase">
          {title}
        </div>
        <div
          style={{ minHeight: '48px' }}
          className="d-flex align-items-center"
        >
          {loading ? (
            <Skeleton width="60%" height={40} />
          ) : (
            <div className={`display-5 fw-bold ${color}`}>{value}</div>
          )}
        </div>
      </Card.Body>
    </Card>
  </Col>
)

const ExpiredItemCard = ({ item, loading = false }) => (
  <Card
    className={`border-0 shadow-sm rounded-4 ${styles.expiredItemCard} ${!loading ? styles.fadeIn : ''}`}
  >
    <Card.Body className="p-4">
      <div style={{ minHeight: '48px' }}>
        {loading ? (
          <Row className="align-items-center">
            <Col md={6}>
              <Skeleton width="80%" height={20} className="mb-1" />
              <Skeleton width="50%" height={15} />
            </Col>
            <Col md={2}>
              <Skeleton width="60%" height={12} className="mb-1" />
              <Skeleton width="80%" height={18} />
            </Col>
            <Col md={2}>
              <Skeleton width="60%" height={12} className="mb-1" />
              <Skeleton width="80%" height={18} />
            </Col>
            <Col md={2}>
              <Skeleton width="60%" height={12} className="mb-1" />
              <Skeleton width="80%" height={18} />
            </Col>
          </Row>
        ) : (
          <Row className="align-items-center">
            <Col md={6}>
              <div className="fw-bold text-dark mb-1">
                {item.item_code} - {item.item_name}
              </div>
              <div className="text-secondary small">
                Batch: {item.batch} | Slot: {item.slot}
              </div>
            </Col>
            <Col md={2}>
              <div
                className={`text-secondary ${styles['x-small']} fw-bold uppercase`}
              >
                Label PD
              </div>
              <div className="fw-bold small">
                {item.report_date.split(' ')[0]}
              </div>
            </Col>
            <Col md={2}>
              <div
                className={`text-secondary ${styles['x-small']} fw-bold uppercase`}
              >
                Label ED
              </div>
              <div className="fw-bold small">
                {item.report_date.split(' ')[0]}
              </div>
            </Col>
            <Col md={2}>
              <div
                className={`text-secondary ${styles['x-small']} fw-bold uppercase`}
              >
                Qty Kemasan
              </div>
              <div className="fw-bold small">{item.total_pack}</div>
            </Col>
          </Row>
        )}
      </div>
    </Card.Body>
  </Card>
)

const SLOT_TYPE_CONFIG = {
  rack: { label: 'Rack', color: '#F7931E', bg: '#fff5e6' },
  ground: { label: 'Ground', color: '#3B82F6', bg: '#eff6ff' },
  double: { label: 'Double Deep', color: '#8B5CF6', bg: '#f5f3ff' },
  shelving: { label: 'Shelving', color: '#10B981', bg: '#f0fdf4' },
  unknown: { label: 'Other', color: '#6B7280', bg: '#f9fafb' }
}

const SlotCapacitySection = ({ data, loading }) => {
  if (loading) {
    return (
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-4">
          <Row className="mb-4 g-3">
            {[1, 2, 3].map((i) => (
              <Col md={4} key={i}>
                <Skeleton height={80} borderRadius={12} />
              </Col>
            ))}
          </Row>
          {[1, 2, 3].map((i) => (
            <div key={i} className="mb-3">
              <Skeleton width="30%" height={14} className="mb-2" />
              <Skeleton height={10} borderRadius={10} />
            </div>
          ))}
        </Card.Body>
      </Card>
    )
  }

  if (!data) {
    return (
      <Card className="border-0 shadow-sm rounded-4 p-3 text-center text-muted small">
        No slot capacity data available
      </Card>
    )
  }

  const usagePct = data.usage_percentage ?? 0
  const usageColor =
    usagePct >= 90 ? '#EF4444' : usagePct >= 70 ? '#F7931E' : '#22C55E'

  return (
    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
      <Card.Body className="p-4">
        <Row className="mb-4 g-3">
          <Col md={4}>
            <div
              className="rounded-3 p-3 text-center"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <div
                className="fw-bold"
                style={{ fontSize: '28px', color: '#1e293b' }}
              >
                {data.total_slots.toLocaleString()}
              </div>
              <div className="text-secondary small fw-semibold">
                TOTAL SLOTS
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div
              className="rounded-3 p-3 text-center"
              style={{ backgroundColor: '#fef2f2' }}
            >
              <div
                className="fw-bold"
                style={{ fontSize: '28px', color: '#EF4444' }}
              >
                {data.used_slots.toLocaleString()}
              </div>
              <div
                className="small fw-semibold"
                style={{ color: '#EF4444', opacity: 0.8 }}
              >
                USED
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div
              className="rounded-3 p-3 text-center"
              style={{ backgroundColor: '#f0fdf4' }}
            >
              <div
                className="fw-bold"
                style={{ fontSize: '28px', color: '#22C55E' }}
              >
                {data.available_slots.toLocaleString()}
              </div>
              <div
                className="small fw-semibold"
                style={{ color: '#22C55E', opacity: 0.8 }}
              >
                AVAILABLE
              </div>
            </div>
          </Col>
        </Row>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="small fw-semibold text-secondary">
              Overall Usage
            </span>
            <span className="small fw-bold" style={{ color: usageColor }}>
              {usagePct}%
            </span>
          </div>
          <div
            style={{
              height: '12px',
              backgroundColor: '#f1f5f9',
              borderRadius: '100px',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${usagePct}%`,
                height: '100%',
                backgroundColor: usageColor,
                borderRadius: '100px',
                transition: 'width 0.6s ease'
              }}
            />
          </div>
        </div>

        {data.by_type && data.by_type.length > 0 && (
          <div>
            <div className="text-secondary small fw-semibold mb-3 text-uppercase">
              By Slot Type
            </div>
            <Row className="g-3">
              {data.by_type.map((type) => {
                const cfg =
                  SLOT_TYPE_CONFIG[type.slot_type] || SLOT_TYPE_CONFIG.unknown
                const pct = type.usage_percentage ?? 0
                return (
                  <Col md={6} key={type.slot_type}>
                    <div
                      className="rounded-3 p-3"
                      style={{
                        backgroundColor: cfg.bg,
                        border: `1px solid ${cfg.color}22`
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span
                          className="fw-semibold small"
                          style={{ color: cfg.color }}
                        >
                          {cfg.label}
                        </span>
                        <span
                          className="small fw-bold"
                          style={{ color: cfg.color }}
                        >
                          {pct}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: '8px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '100px',
                          overflow: 'hidden',
                          marginBottom: '8px'
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            backgroundColor: cfg.color,
                            borderRadius: '100px',
                            transition: 'width 0.6s ease'
                          }}
                        />
                      </div>
                      <div className="d-flex justify-content-between">
                        <span
                          className="text-muted"
                          style={{ fontSize: '11px' }}
                        >
                          {type.used_slots} used / {type.total_slots} total
                        </span>
                        <span
                          style={{
                            color: '#22C55E',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          {type.available_slots} free
                        </span>
                      </div>
                    </div>
                  </Col>
                )
              })}
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default HomeDashboard
